import {AgentIcon, BookIcon, IdBadgeIcon, PaintbrushIcon, PlugIcon, ServerIcon} from '@primer/octicons-react'
import {advancedSearchPath, getRelativeHref, searchPath} from '@github-ui/paths'
import type React from 'react'
import {useCallback} from 'react'

import type {SearchKindCount} from '../../hooks/use-search-result-counts'
import {useSoftNavigate} from '../../hooks/use-soft-navigate'
import type {AIResourceSearchResult, SearchResults} from '../../types/blackbird-types'
import type {AIResourceResult} from './AIResourceResults'
import {AIResourceSearchPage, type AIResourceSearchPageState} from './AIResourceSearchPage'
import type {ResourceTypeFilterProps} from './ResourceTypeFilter'
import type {AIResourceSearchCategory} from './SearchCategoryNav'

type AIResourceSearchProps = {
  counts: readonly SearchKindCount[]
  payload: SearchResults | null
  query: string
}

const resourceTypes = [
  {label: 'Skill', qualifier: 'type:"Skill"', icon: BookIcon},
  {label: 'MCP Server', qualifier: 'type:"MCP Server"', icon: ServerIcon},
  {label: 'Agent', qualifier: 'type:"Agent"', icon: AgentIcon},
  {label: 'Plugin', qualifier: 'type:"Plugin"', icon: PlugIcon},
  {label: 'A2A agent', qualifier: 'type:"A2A agent"', icon: IdBadgeIcon},
  {label: 'Canvas', qualifier: 'type:"Canvas"', icon: PaintbrushIcon},
] as const

const clearedSearchParameters = {p: null, page_token: null, l: null, s: null, o: null}

export function AIResourceSearch({counts, payload, query}: AIResourceSearchProps) {
  const navigateToPage = useSoftNavigate(searchPath)
  const nextPageToken = payload?.type === 'ai_resources' ? payload.page_token : null
  const nextPageHref = nextPageToken
    ? getRelativeHref(searchPath, undefined, {p: null, page_token: nextPageToken})
    : undefined
  const onNextPage = useCallback(
    (event: React.MouseEvent) => {
      if (!nextPageToken || event.defaultPrevented || event.button !== 0) return
      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic -- preserve native link-modifier behavior
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return

      event.preventDefault()
      window.scrollTo(0, 0)
      navigateToPage(undefined, {p: null, page_token: nextPageToken})
    },
    [navigateToPage, nextPageToken],
  )

  const categories: AIResourceSearchCategory[] = counts.map(count => ({
    label: capitalize(count.readableNamePlural),
    href: getRelativeHref(searchPath, undefined, {...clearedSearchParameters, q: query, type: count.name}),
    icon: count.icon,
    count: count.final && !count.error ? count.total : undefined,
    current: count.name === 'ai_resources',
  }))

  const filters: ResourceTypeFilterProps = {
    resourceTypes: resourceTypes.map(resourceType => ({
      label: resourceType.label,
      href: getFilterHref(query, resourceType.qualifier),
      icon: resourceType.icon,
    })),
    labelFilterHref: getFilterHref(query, 'label:'),
    advancedSearchHref: getRelativeHref(advancedSearchPath, undefined, {q: query}),
  }

  return (
    <AIResourceSearchPage
      categories={categories}
      filters={filters}
      onNextPage={onNextPage}
      query={query}
      state={getState(payload, nextPageHref)}
    />
  )
}

function getFilterHref(query: string, qualifier: string) {
  return getRelativeHref(searchPath, undefined, {
    ...clearedSearchParameters,
    q: `${query} ${qualifier}`.trim(),
    type: 'ai_resources',
  })
}

function getState(payload: SearchResults | null, nextPageHref?: string): AIResourceSearchPageState {
  if (!payload) return {status: 'loading'}
  if (payload.errors.length > 0) return {status: 'error'}
  if (payload.type !== 'ai_resources') return {status: 'empty'}

  const results = payload.results.flatMap(result => {
    const mappedResult = mapResult(result)
    return mappedResult ? [mappedResult] : []
  })
  if (results.length === 0 && !nextPageHref) return {status: 'empty'}

  return {
    status: 'success',
    results,
    totalCount: payload.result_count > 0 ? payload.result_count : undefined,
    nextPageHref,
  }
}

function mapResult(result: AIResourceSearchResult): AIResourceResult | null {
  if (!isSafeUrl(result.href)) return null

  const publisher = result.publisher
  return {
    id: result.id,
    href: result.href,
    name: result.name,
    description: result.description ?? undefined,
    resourceType: result.resource_type,
    labels: result.labels,
    publisher: publisher && isSafeUrl(publisher.href) ? publisher : undefined,
  }
}

function isSafeUrl(value: string | null): value is string {
  if (!value || !URL.canParse(value)) return false

  try {
    // A base would allow relative values, and browser globals are unavailable during SSR.
    // eslint-disable-next-line no-restricted-syntax
    const {protocol} = new URL(value)
    return protocol === 'https:' || protocol === 'http:'
  } catch {
    return false
  }
}

function capitalize(value: string) {
  return `${value.charAt(0).toUpperCase()}${value.slice(1)}`
}
